import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 70;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4o57i9btbscp07qbi6g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', company: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 shadow-sm' : 'bg-transparent'}`}>
        <div className="px-5 md:px-10 lg:px-20 h-[60px] md:h-[70px] flex items-center justify-between">
          <div className="flex items-center">
            <h1 className={`text-2xl md:text-3xl font-serif italic transition-colors ${isScrolled ? 'text-[#1A2B3C]' : 'text-white'}`} style={{ fontFamily: "'Playfair Display', serif" }}>
              Future Field
            </h1>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8 xl:gap-12">
            <button onClick={() => scrollToSection('hero')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-[#1A2B3C]' : 'text-white hover:text-white/80'}`}>トップ</button>
            <button onClick={() => scrollToSection('message')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-[#1A2B3C]' : 'text-white hover:text-white/80'}`}>メッセージ</button>
            <button onClick={() => scrollToSection('news')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-[#1A2B3C]' : 'text-white hover:text-white/80'}`}>ニュース</button>
            <button onClick={() => scrollToSection('services')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-[#1A2B3C]' : 'text-white hover:text-white/80'}`}>サービス</button>
            <button onClick={() => scrollToSection('company')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-[#1A2B3C]' : 'text-white hover:text-white/80'}`}>会社概要</button>
            <button onClick={() => scrollToSection('contact')} className="bg-[#1A2B3C] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#2A3B4C] transition-colors cursor-pointer whitespace-nowrap">お問い合わせ</button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-white'}`}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white shadow-lg">
            <div className="px-5 py-4 space-y-3">
              <button onClick={() => scrollToSection('hero')} className="block w-full text-left py-2 text-sm font-medium text-gray-700 hover:text-[#1A2B3C] cursor-pointer">トップ</button>
              <button onClick={() => scrollToSection('message')} className="block w-full text-left py-2 text-sm font-medium text-gray-700 hover:text-[#1A2B3C] cursor-pointer">メッセージ</button>
              <button onClick={() => scrollToSection('news')} className="block w-full text-left py-2 text-sm font-medium text-gray-700 hover:text-[#1A2B3C] cursor-pointer">ニュース</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left py-2 text-sm font-medium text-gray-700 hover:text-[#1A2B3C] cursor-pointer">サービス</button>
              <button onClick={() => scrollToSection('company')} className="block w-full text-left py-2 text-sm font-medium text-gray-700 hover:text-[#1A2B3C] cursor-pointer">会社概要</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-center bg-[#1A2B3C] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#2A3B4C] transition-colors cursor-pointer mt-2">お問い合わせ</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative h-[70vh] md:h-[80vh] lg:h-[85vh] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/27060dff0b6c8bc3768ddccafdf8de36/b05a9c92b5b70d5af16b13c42a381b4f.jpeg" 
            alt="Hero Background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 px-5 md:px-10 lg:pl-32 max-w-4xl w-full">
          <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold text-white leading-tight mb-4 md:mb-6">
            Connect the Future.<br />
            <span className="text-2xl md:text-4xl lg:text-6xl">― 未来をつなぐ</span>
          </h1>
          <p className="text-base md:text-lg lg:text-xl text-white/90 max-w-2xl leading-relaxed">
            継続的な成果につながる伴走型の支援を目指しています
          </p>
        </div>
      </section>

      {/* Message Section */}
      <section id="message" className="bg-white py-16 md:py-24 lg:py-36 px-5 md:px-10 lg:px-32">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-center mb-6 md:mb-10">
            <div className="bg-gray-100 rounded-full px-4 md:px-6 py-1.5 md:py-2">
              <span className="text-xs font-semibold text-gray-600 tracking-widest">MESSAGE</span>
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A2B3C] text-center mb-10 md:mb-16 leading-tight" style={{ fontFamily: 'serif' }}>
            成果を共に描く、<br />伴走型のマーケティングパートナー
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-20 mb-8 md:mb-12">
            <div className="text-gray-700 text-sm md:text-base leading-relaxed space-y-4 md:space-y-6">
              <p>
                WebメディアやSNSを“単なる運用”で終わらせず、
成果へ導くストーリーを共に設計する。
それが future field のスタイルです。
              </p>
              <p>
                私たちは、ユーザーとのコミュニケーションが何より重要ととらえ、クライアント様のブランド価値を高め、ファンとつながるWebコミュニケーションをデザインします。
              </p>
            </div>
            <div className="text-gray-700 text-sm md:text-base leading-relaxed space-y-4 md:space-y-6">
              <p>
                ・単なる投稿や運用ではなく 成果に直結する施策<br />
                ・数字に向き合う運用を重視<br />
                ・市場・競合・ユーザーを深く理解し、クライアント様の目標に寄り添う支援<br />
                ・柔軟かつ迅速な対応で成長を後押しします
              </p>
              <p>
                私たちは一過性の効果ではなく、継続的な成果につながる伴走型の支援を目指しています。
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <div className="text-right">
              <p className="text-xs md:text-sm text-gray-500 italic">代表取締役社長</p>
              <p className="text-xs md:text-sm text-gray-500 italic mt-1">原 裕介</p>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section id="news" className="bg-[#F5F5F7] py-16 md:py-24 lg:py-32 px-5 md:px-10 lg:px-32">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1A2B3C]">NEWS</h2>
          </div>
          <div className="space-y-0">
            <div className="border-b border-gray-300">
              <div className="flex flex-col md:flex-row md:items-center py-5 md:py-0 md:h-24 gap-3 md:gap-8">
                <div className="w-full md:w-28 text-xs md:text-sm text-gray-600 font-medium">2026.01.01</div>
                <div className="flex-1 text-sm md:text-base text-[#1A2B3C] leading-relaxed">コーポレートサイトをオープンしました。</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="bg-white py-16 md:py-24 lg:py-36 px-5 md:px-10 lg:px-32">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-20">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A2B3C] mb-4 md:mb-6">SERVICES</h2>
            <p className="text-sm md:text-base text-gray-600">お客様のビジネス課題に応じた、最適なソリューションを提供します</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 max-w-5xl mx-auto">
            {[
              {
                title: 'WEBメディア運用サービス',
                description: 'Webメディアは、ユーザーの信頼を育てる重要なタッチポイントです。future field では、コンテンツ企画、SEO対策、アクセス解析を組み合わせて、効果的な運用を実現します。戦略的な編集・改善サイクルで、長期的な成長を支えます。',
                bgColor: 'bg-[#E8EDF2]',
                icon: 'ri-global-line'
              },
              {
                title: 'SNSマーケティング運用・代行',
                description: 'SNSはブランドの顔であり、ユーザーとの双方向コミュニケーションの場です。SNS運用の企画から投稿代行、効果測定・改善まで一貫して対応。プラットフォームごとの特性を活かし、最適なクリエイティブとメッセージで支持を獲得します。',
                bgColor: 'bg-[#F5F0E8]',
                icon: 'ri-chat-smile-3-line'
              }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <div className={`${service.bgColor} h-48 md:h-56 flex items-center justify-center`}>
                  <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center">
                    <i className={`${service.icon} text-[#1A2B3C] text-5xl md:text-6xl`}></i>
                  </div>
                </div>
                <div className="p-8 md:p-10">
                  <h3 className="text-xl md:text-2xl font-bold text-[#1A2B3C] text-center mb-5 md:mb-6">{service.title}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Section */}
      <section id="company" className="bg-[#F5F5F7] py-16 md:py-24 lg:py-32 px-5 md:px-10 lg:px-32">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-16">
            <div className="lg:col-span-2">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1A2B3C] mb-6 md:mb-10">COMPANY</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                会社概要
              </p>
            </div>
            <div className="lg:col-span-3">
              <div className="space-y-0">
                {[
                  { label: '会社名', value: '株式会社フューチャーフィールド' },
                  { label: '設立', value: '2025年2月25日' },
                  { label: '代表者', value: '代表取締役 原裕介' },
                  { label: '資本金', value: '10万円' },
                  { label: '所在地', value: '東京都府中市宮西町4-12-5' },
                  { label: '事業内容', value: 'WEBサービス運用業務、SNSマーケティングの運用・代行業務' }
                ].map((item, index, array) => (
                  <div key={index} className={`flex flex-col md:flex-row min-h-[60px] md:items-center py-3 md:py-0 ${index !== array.length - 1 ? 'border-b border-gray-300' : ''}`}>
                    <div className="w-full md:w-44 text-xs md:text-sm text-gray-600 font-medium mb-1 md:mb-0">{item.label}</div>
                    <div className="flex-1 text-xs md:text-sm text-[#1A2B3C]">{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-[#1A2B3C] py-16 md:py-24 lg:py-36 px-5 md:px-10 lg:px-32">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 md:mb-20">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">CONTACT</h2>
            <p className="text-sm md:text-base text-white/80 leading-relaxed">
              「未来をつなぐ第一歩を、ここから。」<br />
              お問い合わせ・ご相談はお気軽に。
            </p>
          </div>
          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
            <div>
              <label className="block text-white text-sm font-medium mb-2.5">お名前 *</label>
              <input 
                type="text" 
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full h-12 bg-white rounded-lg px-5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-white/50" 
                placeholder="山田 太郎"
              />
            </div>
            <div>
              <label className="block text-white text-sm font-medium mb-2.5">会社名</label>
              <input 
                type="text" 
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="w-full h-12 bg-white rounded-lg px-5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-white/50" 
                placeholder="株式会社サンプル"
              />
            </div>
            <div>
              <label className="block text-white text-sm font-medium mb-2.5">メールアドレス *</label>
              <input 
                type="email" 
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full h-12 bg-white rounded-lg px-5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-white/50" 
                placeholder="example@company.com"
              />
            </div>
            <div>
              <label className="block text-white text-sm font-medium mb-2.5">電話番号</label>
              <input 
                type="tel" 
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full h-12 bg-white rounded-lg px-5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-white/50" 
                placeholder="03-1234-5678"
              />
            </div>
            <div>
              <label className="block text-white text-sm font-medium mb-2.5">お問い合わせ内容 *</label>
              <textarea 
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                maxLength={500}
                className="w-full h-36 md:h-44 bg-white rounded-lg px-5 py-4 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-white/50 resize-none" 
                placeholder="お問い合わせ内容をご記入ください（500文字以内）"
              ></textarea>
              <div className="text-right text-xs text-white/60 mt-2">{formData.message.length}/500</div>
            </div>
            {submitStatus === 'success' && (
              <div className="bg-green-500/20 border border-green-500 text-white px-6 py-4 rounded-lg text-sm">
                お問い合わせを受け付けました。担当者より折り返しご連絡いたします。
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="bg-red-500/20 border border-red-500 text-white px-6 py-4 rounded-lg text-sm">
                送信に失敗しました。もう一度お試しください。
              </div>
            )}
            <div className="flex justify-center pt-4">
              <button 
                type="submit"
                disabled={isSubmitting}
                className="group bg-white rounded-full h-12 md:h-14 px-8 md:px-10 flex items-center gap-3 md:gap-4 hover:scale-105 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <span className="text-[#1A2B3C] font-medium text-sm md:text-base">{isSubmitting ? '送信中...' : '送信する'}</span>
                <div className="w-8 h-8 md:w-10 md:h-10 bg-[#1A2B3C] rounded-full flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white text-base md:text-lg"></i>
                </div>
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A2B3C] border-t border-white/10 py-12 md:py-16 px-5 md:px-10 lg:px-32">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-8 md:mb-12">
            <div>
              <h2 className="text-2xl md:text-3xl font-serif italic text-white mb-4 md:mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Future Field
              </h2>
              <p className="text-sm text-white/70 leading-relaxed">
                ビジネスの未来を共に創造する
              </p>
            </div>
            <div>
              <h3 className="text-sm font-bold text-white mb-4 md:mb-5">MENU</h3>
              <div className="space-y-2 md:space-y-3">
                {['トップ', 'メッセージ', 'ニュース', 'サービス', '会社概要'].map((item, index) => (
                  <button 
                    key={index}
                    onClick={() => scrollToSection(['hero', 'message', 'news', 'services', 'company'][index])}
                    className="block text-sm text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 md:pt-8">
            <p className="text-xs text-white/60 text-center">© 2026 Future Field. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
